import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from 'shared/api';
import { searchRoleSpecialties } from 'shared/api/entities';
import { Specialty } from 'shared/types';

import ServerSearchAutocomplete from '../ServerSearchAutocomplete';
import { ServerAutocompleteProps } from '../ServerSearchAutocomplete/ServerSearchAutocomplete';

type SpecialtyAutocompleteProps<Multiple extends boolean = false> = Omit<ServerAutocompleteProps<Specialty, Multiple>,
    'getOptionLabel' |
    'search'
> & {
    /** Scopes the speciality search to a role. `null` searches all specialities. */
    roleId?: string | null;
    /** Prefetch suggestions on mount. Defaults to true. */
    preloadOnMount?: boolean;
    /** Prefetch suggestions when the dropdown is first opened instead of on mount. */
    preloadOnOpen?: boolean;
};

export default function SpecialtyAutocomplete<Multiple extends boolean = false>({
    multiple,
    roleId = null,
    preloadOnMount = true,
    preloadOnOpen,
    ...rest
}: SpecialtyAutocompleteProps<Multiple>) {
    const { t } = useTranslation('common');

    // Memoised on roleId so useServerSearch re-fetches role-scoped options when the selected role changes.
    const fetcher = useCallback(
        async (query: string, signal?: AbortSignal): Promise<Specialty[] | ApiError> => {
            const response = await searchRoleSpecialties(query, roleId, undefined, undefined, signal);

            if (response instanceof ApiError) {
                return response;
            }

            return response.items;
        },
        [ roleId ]
    );

    return (
        <ServerSearchAutocomplete<Specialty, Multiple>
            size="small"
            multiple={ multiple }
            label={ multiple ? t('commonEntities.specialities') : t('commonEntities.speciality') }
            search={{
                fetcher,
                minLength: 2,
                preloadOnMount,
                preloadOnOpen
            }}
            { ...rest }
        />
    );
}
